// District insights dashboard (v1) — route: /admin/insights
//
// Structured around the questions a district administrator brings, not the
// metrics we compute:
//
//   Overview     "is anything wrong?"        (landing digest)
//   /adoption    "is ABii used, and is it working?"   two lenses:
//                  Outliers — which units sit far from normal (reach x depth)
//                  Usage    — how much ABii the roster gets, and what it buys
//   /learning    "what are students learning?"
//   /fleet       "is the fleet right-sized?"
//
// Adoption keeps school-level and roster-level usage under one page on purpose:
// both lenses are the same usage x impact join at two granularities, and pulling
// them apart leaves the roster lens a denominator report with the impact
// stripped out. The legacy /schools and /engagement paths redirect onto the lens
// each maps to.
//
// Scope framework: this container resolves ONE scope (an organization — an
// org admin's own, or a superadmin's pick) and one school-year window, loads
// the shared data bundle once, and routes between concern pages that all read
// from it. A principal/teacher dashboard is intended to be this same shape
// with a narrower scope and different nouns — new container, same pages.
//
// Data spine — a barrier before any page renders, so keep it to what every page
// needs:
//   accounts/orgunits/                — the org's buildings
//   stats/impact/windowed/segmented/  — per-segment mastery series + district
//   stats/usage/robots/windowed/      — per-robot activity classification
//
// One barrier, but three effects, each keyed on only what it actually varies
// with, so a breakdown-pill toggle doesn't re-fetch the orgunit list and the
// whole fleet payload to answer a question neither depends on. Only the
// segmented call varies with `dimension`/`selectedSchool`; the fleet varies with
// the org and the window; the buildings vary with the org alone. Repeat requests
// are also served from an in-tab cache (services/statsCache), so returning to a
// breakdown already viewed costs nothing.
//
// Fetched outside the barrier, each rendering a placeholder until it lands:
//   stats/engagement/windowed/  — dosage-tier composition (here, lazily)
//   stats/mastery/units/        — unit gaps (in OverviewPage)
//   the per-tier mastery fan-out (in the Usage lens)
//
// Engagement is deliberately not part of the spine: it runs ~90s on the largest
// district, and the landing page wants only the "Busiest month" rail stat from
// it. Its server cost is tracked in EngagementService.compose_series, but a call
// that heavy does not belong in front of first paint whatever it costs.
//
// URL overrides (all optional): ?standardset=
// ?interval=day|week|month ?start=YYYY-MM-DD ?end=YYYY-MM-DD ?organization=<id>
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Switch, Route, Redirect } from "react-router-dom";
import FA from "../fa";
import withAPI from "../../services/api";
import config from "../../common/config";
import { useTitle } from "../../common/utils";
import { getAllSchoolInfo } from "../../components/InputFields/helpers";
import { STATUS, SEG_NOUN } from "../impact/tokens";
import {
  buildSeries,
  buildRecent,
  toSegmentRows,
  buildQuad,
} from "../impact/model";
import {
  syLabel,
  syWindow,
  defaultGrain,
  RECONSTRUCTED_NOTE,
  NO_HISTORY_MESSAGE,
} from "../impact/schoolyear";
import { withParam } from "../impact/format";
import { withKinds } from "../impact/status";
import { useDashboardScope } from "./scope";
import { recentState } from "../impact/fleet";
import {
  YearSelect,
  OrgScopeSelect,
  ExportButton,
  isAllOrgs,
} from "../impact/chrome";
import { DashShell } from "./chrome";
import { OverviewPage } from "./OverviewPage";
import {
  SegmentControls,
  AdoptionPage,
  LearningPage,
  FleetPage,
} from "./pages";

// Legacy standalone paths -> the Adoption lens each one maps to.
const LEGACY_LENS = { "/schools": "outliers", "/engagement": "usage" };

// The all-organizations scope serves the Overview only, so it gets no nav bar at
// all. Hidden rather than shown-and-disabled: the other three pages are not
// "loading" or "empty" there, they are questions this scope does not answer, and
// three dead pills say that worse than the shell's caveat does. An empty array
// rather than a one-item nav for the same reason DashShell takes one -- a lone
// pill reading "Overview ›" that links to the page you are on is furniture.
const ALL_ORGS_NAV = [];

const DistrictDashboard = ({ api, user, match, location, history }) => {
  useTitle("District insights");
  const [state, setState] = useState({ loading: true });

  // Organization, school year and roster provenance -- the shared hook, also
  // used by RenewalBrief. See ./scope.
  const {
    syncParams,
    isSuperadmin,
    selectedOrg,
    selectOrg,
    org,
    today,
    curSY,
    schoolYear,
    selectSchoolYear: handleSetSchoolYear,
    yearOptions,
    coverage,
    dimensionsAvailable,
    noteLedger: noteLedgerMeta,
    resetCoverage,
  } = useDashboardScope({ api, user, history });

  // Breakdown dimension, seeded from and written back to ?breakdown= so a
  // worklist drill-down stays linkable ("the Grade 1 cohort by school").
  // Unknown values fall back rather than 404-ing a shared link. "engagement"
  // is a drill target from the Engagement page, not a pill the admin picks.
  const DIMENSIONS = ["school", "grade", "teacher", "engagement"];
  const [dimension, setDimension] = useState(() => {
    const d = new URLSearchParams(window.location.search).get("breakdown");
    return DIMENSIONS.includes(d) ? d : "school";
  });
  // ALL-ORGANIZATIONS scope (superadmin's default landing -- see ./scope). One
  // flag, derived from the resolved org, because everything that has to change
  // changes together:
  //
  //   - the peer set is districts, not schools, so the breakdown is fixed at
  //     `organization` (the only cross-district split the server admits, along
  //     with grade and engagement -- see ALL_ORGS_DIMENSIONS),
  //   - the buildings list and the school picker are per-district facts with
  //     no cross-district answer, so they are skipped rather than fetched and
  //     shown empty,
  //   - only the Overview is served: Adoption's worklist is a school/teacher
  //     comparison and Learning's cohorts are a district's standardset, and
  //     neither has a cross-district reading yet.
  //
  // Deliberately NOT a separate container. The window, the year picker, the
  // provenance envelope, the spine and every Overview panel are identical; what
  // differs is which learners are in scope, which is what a scope is.
  //
  // COST, measured cold-cache against a local copy of prod (317 orgs, 29,709
  // learners in the SY26-27 roster, 2,708 robots), as the sum of the four reads
  // this page makes: 6.3s all-orgs vs 2.4s for Blue Springs, the largest single
  // district, and ~0.4-0.6s for a mid-sized one. So it is roughly 2.6x the
  // worst district rather than 317x one -- the roster is one `__in` (see
  // orgs_members_during) and the per-segment mastery fold was already one pass
  // over one scan. Against the Azure DB the absolute numbers are larger; the
  // ratio is what carries. Only the segmented call and the fleet call are in
  // front of first paint; the engagement mix and the unit gaps fill in after.
  // `org` already resolves the org-admin's pinned district vs the superadmin's
  // pick, and a real district's id is a number, so this cannot fire for anyone
  // who did not choose the sentinel.
  const allOrgs = isAllOrgs(org);
  // What the server is asked to split by. Held apart from `dimension` (the
  // control's own state) so returning to a district restores the breakdown the
  // admin had chosen rather than leaving them on a dimension the pills don't
  // offer.
  const loadDimension = allOrgs ? "organization" : dimension;
  const [selectedSchool, setSelectedSchool] = useState(
    () => new URLSearchParams(window.location.search).get("school") || null
  ); // teacher dim only
  // Tracks whether the admin has *manually* chosen a breakdown dimension. Once
  // they have, we stop auto-overriding it (see the single-building flip below).
  // Automatic corrections (grade->school, single-building flip) go through the
  // bare setDimension and deliberately leave this false. A deep-linked
  // ?breakdown= is an explicit choice, so it seeds this as touched.
  const dimensionTouched = useRef(
    DIMENSIONS.includes(
      new URLSearchParams(window.location.search).get("breakdown")
    )
  );
  const handleSetDimension = (d) => {
    dimensionTouched.current = true;
    setDimension(d);
    syncParams({ breakdown: d });
  };
  // Wrapped school setter that mirrors the teacher-dimension school scope to
  // the URL -- used by both the UI picker and the load effect's auto-correction.
  const handleSelectSchool = (school) => {
    setSelectedSchool(school);
    syncParams({ school });
  };

  // Changing org drops this container's own scope state with it: a school id
  // means nothing in the new district, and the breakdown default is re-evaluated
  // so the single-building check below runs fresh against the new org's units (only
  // if the dimension was auto-flipped rather than user-chosen).
  const handleSelectOrg = (o) => {
    setSelectedSchool(null);
    if (!dimensionTouched.current) setDimension("school");
    selectOrg(o, { school: null });
  };

  // Role flags for the curriculum-view controls (decision built below, once the
  // .as_user-scoped orgunit list is in hand). The standard set is a
  // superadmin-only lever; the school picker's audience is worked out at render.
  const isOrgAdmin = user && user.usertype === config.ORGADMIN_USERTYPE;
  const isOrgUnitAdmin = user && user.usertype === config.ORGUNITADMIN_USERTYPE;

  // Named rather than spelled `org && org.id` inside each dependency array: the
  // three loads below key on identity, and an expression in a dep array is one
  // the exhaustive-deps rule cannot check statically.
  const orgId = org && org.id;
  const selectedOrgId = selectedOrg && selectedOrg.id;

  // ---- school-year window --------------------------------------------------
  // The year itself comes from useDashboardScope; the window derived from it is
  // this container's own, because it honours ?interval= / ?end= overrides and
  // needs a separate utilInterval for the bucketed strips.
  const windowSpec = useMemo(() => {
    const qp = new URLSearchParams(window.location.search);
    const ssParam = qp.get("standardset") || undefined;
    const { start, end: syEnd } = syWindow(schoolYear, curSY, today);
    const end = schoolYear === curSY ? qp.get("end") || syEnd : syEnd;
    // Derived from the window rather than fixed at "month" (see defaultGrain):
    // a school year four weeks old has one monthly bucket, and a one-bucket
    // series is not a series. Ordered after the window on purpose -- the grain
    // is a function of it.
    const interval = qp.get("interval") || defaultGrain(start, end);
    const utilInterval = interval === "day" ? "week" : interval;
    return { start, end, interval, ssParam, utilInterval };
  }, [schoolYear, curSY, today]);


  // ---- the org's buildings -------------------------------------------------
  // Its own effect, and keyed only on the organization, because that is all it
  // depends on. Folding it into the main load would make every breakdown change
  // re-fetch a district's orgunit list to answer a question the breakdown cannot
  // affect -- an uncached round trip in front of the spine.
  //
  // Everything downstream needs it: the teacher breakdown's school scope is
  // corrected against it, the per-OU utilization fallback iterates it, and three
  // pages take it as a prop. So the loads below wait on it rather than racing it.
  const [ouState, setOuState] = useState({ loading: true, orgunits: [] });
  useEffect(() => {
    // Skipped outright under the all-orgs scope: "the buildings" is a district
    // question, and every consumer of this list (the school picker, the teacher
    // breakdown's scope correction, the per-OU utilization fallback) is one of
    // the things that scope switches off. Fetching every OrgUnit in the customer
    // base to populate a picker nothing renders is a page-load cost for nothing.
    if (allOrgs || !org || !org.id) {
      setOuState({ loading: false, orgunits: [] });
      return undefined;
    }
    let cancelled = false;
    setOuState((s) => ({ ...s, loading: true }));
    const params = new URLSearchParams();
    if (isSuperadmin || user.usertype === config.ORGADMIN_USERTYPE) {
      params.append("organization", org.id);
    }
    getAllSchoolInfo(api, params).then((ous) => {
      if (!cancelled) setOuState({ loading: false, orgunits: ous || [] });
    });
    return () => {
      cancelled = true;
    };
    // `api` is recreated every render by withAPI — depending on it never settles
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);
  const orgunits = ouState.orgunits;

  // ---- fleet utilization ---------------------------------------------------
  // Part of the barrier (the Overview's fleet card and the whole Fleet page read
  // it), but NOT part of the breakdown: it is scoped to the organization and the
  // window and knows nothing about `dimension` or the selected school. Keep it
  // off the main load's deps: sharing them makes every pill toggle re-pay it --
  // ~3.7s on Blue Springs for a byte-identical answer, on the single
  // most-clicked control on the page. The engagement mix is split out for the
  // same reason.
  const [utilState, setUtilState] = useState({ loading: true });
  useEffect(() => {
    if (!org || !org.id || ouState.loading) return undefined;
    let cancelled = false;
    setUtilState({ loading: true });
    const { start, end, utilInterval } = windowSpec;
    const utilArgs = { start, end, interval: utilInterval };
    // The whole fleet under the all-orgs scope. `id` still rides along as the
    // sentinel so the request, the cache key and the response's `scope_id` are
    // shaped exactly like a district's.
    const utilScope = allOrgs ? "all" : "organization";
    // Provenance is read off whichever response lands first rather than the
    // barrier -- see useDashboardScope. Wrapped only to add this load's
    // `cancelled` guard, so a superseded load can't write at all.
    const noteLedger = (r) => (cancelled ? r : noteLedgerMeta(r));

    const load = async () => {
      let robotUtil = await api
        .fetchWindowedRobotUtilization({
          scope: utilScope,
          id: org.id,
          ...utilArgs,
        })
        .then(noteLedger)
        .catch(() => null);

      // roles without org-level visibility: merge per-OU utilization. Never
      // reached under the all-orgs scope, which is superadmin-only and has no
      // buildings loaded to merge over.
      if (!robotUtil && !allOrgs) {
        const parts = await Promise.all(
          orgunits.map((ou) =>
            api
              .fetchWindowedRobotUtilization({
                scope: "orgunit",
                id: ou.id,
                ...utilArgs,
              })
              .then(noteLedger)
              .catch(() => null)
          )
        );
        const bySerial = new Map();
        let ruBuckets = null;
        // Carried like `buckets`: the strip's shading explanation is keyed on
        // it, and each part reports the same org's season anyway (detection is
        // per institution, and these are schools of one district).
        let ruSeason = null;
        parts.filter(Boolean).forEach((p) => {
          ruBuckets = ruBuckets || p.buckets;
          ruSeason = ruSeason || p.season || null;
          (p.robots || []).forEach((r) => bySerial.set(r.serial, r));
        });
        robotUtil = ruBuckets
          ? {
              interval: utilInterval,
              buckets: ruBuckets,
              robots: [...bySerial.values()],
              total_robots: bySerial.size,
              season: ruSeason,
            }
          : null;
      }
      if (!cancelled) setUtilState({ loading: false, robotUtil });
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, windowSpec, ouState.loading, orgunits]);

  // ---- main load -----------------------------------------------------------
  // The breakdown itself: the one request that genuinely varies with `dimension`
  // and `selectedSchool`.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setState((s) => ({ ...s, loading: true }));
      // the note renders outside the swapped-out body, so a stale server verdict
      // would keep describing the previous year while the new one loads
      resetCoverage();
      try {
        const { start, end, interval, ssParam } = windowSpec;
        const winArgs = { start, end, interval, standardset: ssParam };
        if (!org || !org.id) {
          if (!cancelled)
            setState({ loading: false, awaitingOrg: isSuperadmin });
          return;
        }
        if (ouState.loading) return; // re-runs on the dep when they land

        // teacher breakdown is scoped to one school; correct an invalid
        // selection and let the dep re-run this effect once
        let school = null;
        if (loadDimension === "teacher") {
          const valid = orgunits.some(
            (o) => String(o.id) === String(selectedSchool)
          );
          school = valid
            ? selectedSchool
            : orgunits.length
            ? String(orgunits[0].id)
            : null;
          if (String(school) !== String(selectedSchool)) {
            if (!cancelled) handleSelectSchool(school);
            return;
          }
        }

        // Provenance is read off whichever response lands first rather than the
        // barrier -- see useDashboardScope. Wrapped only to add this load's
        // `cancelled` guard, so a superseded load can't write at all.
        const noteLedger = (r) => (cancelled ? r : noteLedgerMeta(r));

        const seg = await api
          .fetchSegmentedWindowedImpactStats({
            dimension: loadDimension,
            id: org.id,
            school: loadDimension === "teacher" ? school : undefined,
            // The worklist's status cascade reads both: lesson volume per
            // bucket (Stalled, Bursty, the pattern bar) and the at-target share
            // (Shallow). Two extra server queries for the whole request. The
            // drill-down levels deliberately don't ask — see api.js.
            include: "volume,dosage",
            ...winArgs,
          })
          .then(noteLedger)
          .catch(() => null);

        const orgWin = seg ? seg.district : null;
        const schools = toSegmentRows(seg && seg.segments).filter(
          (s) => s.roster > 0
        );

        // ledger_start / coverage are recorded by noteLedger as each response
        // lands — nothing left to do here.

        if (!cancelled)
          setState({
            loading: false,
            schools,
            orgSeries: buildSeries(orgWin),
            // Beside the series, not in it -- see buildRecent. The Overview's
            // movement card reads this instead of differencing the last two
            // boundaries of orgSeries.
            orgRecent: buildRecent(orgWin),
            orgRoster:
              orgWin && orgWin.roster_size != null ? orgWin.roster_size : null,
            window: winArgs,
            standardset: (orgWin && orgWin.standardset) || ssParam || null,
          });
      } catch (err) {
        console.error("District dashboard load failed:", err);
        if (!cancelled) setState({ loading: false, error: String(err) });
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // `api` is re-created every render by the withAPI HOC — depending on it
    // would re-fire endlessly; user/today/curSY are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    schoolYear,
    loadDimension,
    selectedSchool,
    selectedOrgId,
    ouState.loading,
    orgunits,
  ]);

  // ---- engagement mix, outside the barrier --------------------------------- #
  // The slowest call in the dashboard by a wide margin, and the only page that
  // renders on arrival needs it for ONE rail stat. Kept out of the load above so
  // first paint doesn't wait on it: consumers get `comp: null` plus
  // `compLoading`, and fill in when it lands. Same shape as the Overview's
  // unit-mastery fetch, which is lazy for the same reason.
  //
  // Narrower deps than the main load on purpose. This payload is scoped to the
  // organization and the window — it knows nothing about `dimension` or the
  // selected school — so re-running it on a breakdown change would re-pay the
  // most expensive request in the page for an identical answer.
  const [compState, setCompState] = useState({ loading: true });
  useEffect(() => {
    if (!org || !org.id) return undefined;
    let cancelled = false;
    setCompState({ loading: true });
    const { start, end, utilInterval } = windowSpec;
    api
      .fetchWindowedEngagement({
        scope: allOrgs ? "all" : "organization",
        id: org.id,
        start,
        end,
        interval: utilInterval,
      })
      .then((res) => {
        if (cancelled) return;
        setCompState({ loading: false, comp: res });
        // The spine calls normally report provenance first and identically, so
        // this is only load-bearing if both of them failed — in which case it is
        // the difference between offering past years and claiming there are none.
        noteLedgerMeta(res);
      })
      .catch(() => {
        if (!cancelled) setCompState({ loading: false, failed: true });
      });
    return () => {
      cancelled = true;
    };
    // `api` is recreated every render by withAPI — depending on it never settles
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org && org.id, windowSpec]);
  const comp = compState.comp || null;
  const compLoading = !!compState.loading;
  const compFailed = !!compState.failed;

  const {
    loading: breakdownLoading,
    error,
    awaitingOrg,
    schools = [],
    orgSeries,
    orgRecent,
    orgRoster,
  } = state;
  const robotUtil = utilState.robotUtil;
  // The barrier, reassembled from the three loads above. Every page reads both
  // halves -- the Overview's digest pairs a breakdown row with the fleet card --
  // so a page rendered on one of them would show a panel filling in under the
  // reader. Splitting the FETCHES so a breakdown change doesn't re-pay for the
  // fleet is the point; splitting the barrier is not.
  const loading = breakdownLoading || utilState.loading || ouState.loading;
  const noun = SEG_NOUN[loadDimension];

  // A breakdown the ledger can't answer for this window is corrected away from
  // rather than left to fail. Two ways to arrive here: switching to a past year
  // while the teacher breakdown is selected, or opening a stale ?breakdown=teacher
  // deep link on one. In the second case the segmented request 400s once before
  // this fires -- the other spine call carries dimensions_available and always
  // succeeds, so the page self-heals on the next pass rather than sitting on an
  // error.
  //
  // Bare setDimension, like the single-building flip below: this is a correction,
  // not a choice, so it must not mark the dimension user-touched. ?breakdown= is
  // cleared so a reload doesn't repeat the 400.
  useEffect(() => {
    // Under the all-orgs scope the served dimension is fixed and the control is
    // not on screen, so there is nothing to correct -- and correcting anyway
    // would overwrite the district breakdown the admin had chosen.
    if (allOrgs) return;
    if (!dimensionsAvailable || dimensionsAvailable.includes(dimension)) return;
    setDimension(
      dimensionsAvailable.includes("school")
        ? "school"
        : dimensionsAvailable[0] || "school"
    );
    syncParams({ breakdown: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensionsAvailable, dimension]);

  // Single-building districts: a "by school" breakdown yields one segment, so
  // the reach x depth quadrant and its outlier detection are degenerate. Default
  // to a teacher breakdown instead, which slices the one building into useful
  // rows. Only overrides the initial school default -- a manual dimension pick
  // sticks (dimensionTouched), and switching org resets it (handleSelectOrg).
  // The flip re-runs the main load via the `dimension` dep; selectedSchool then
  // auto-resolves to the sole orgunit inside that load.
  //
  // Gated on the replacement actually being answerable for this window, because
  // otherwise this correction and the one above contradict each other and neither
  // yields: on a single-building district viewed on a past year, this flips
  // school->teacher, the effect above flips teacher->school (the ledger can't
  // attribute who taught then), and each flip re-runs the main load through the
  // `dimension` dep -- an unbounded render loop re-firing the whole spine, with
  // the segmented request 400ing every second pass. Both corrections are right
  // about their own concern; availability is the one that has to win, since a
  // degenerate breakdown is still readable and an unanswerable one is not.
  // Waiting for `dimensionsAvailable` also keeps the flip from happening before
  // the first response, so it can't fire on a window it will immediately be
  // reversed on.
  //
  // Grade is the second choice, and it is what a single-building district lands on
  // for any past year: teacher attribution needs full ledger coverage and the
  // August rollover rewrites those links every year, but grade is recoverable much
  // further back (the auditlog backfill carried LearnerGradeInterval to 2023), so
  // it slices the one building into real cohorts where teacher can't. Teacher is
  // still preferred when both are available -- rooms are the unit a single school's
  // admin acts on, grades cut across them.
  useEffect(() => {
    if (
      dimensionTouched.current ||
      dimension !== "school" ||
      orgunits.length !== 1 ||
      !dimensionsAvailable
    )
      return;
    const flipTo = ["teacher", "grade"].find((d) =>
      dimensionsAvailable.includes(d)
    );
    // Nothing to flip to (a window that can answer neither) leaves the degenerate
    // one-segment school view standing, which is still readable.
    if (flipTo) setDimension(flipTo);
  }, [dimension, orgunits, dimensionsAvailable]);

  // The school picker: super/org admins always get it (defaults to "all
  // schools", narrows to one). An orgunitadmin only benefits when .as_user
  // actually scopes them to more than one building — otherwise the sole option
  // is redundant with the default, so hide it.
  const canScopeSchool =
    isSuperadmin || isOrgAdmin || (isOrgUnitAdmin && orgunits.length > 1);

  // Working robots per OrgUnit and per teacher — the capacity signal behind the
  // "Under-resourced" status, which is checked BEFORE any rule implying a
  // teaching problem so nobody is sent to PD when the answer is dark robots.
  //
  // No new endpoint or join for either: the robot-utilization payload the Fleet
  // page already loads carries `orgunits: [{id, name}]` and `teachers: [id]` per
  // robot. A robot serving two buildings counts for both, which is the right
  // reading of "how much working hardware does this school have access to", and
  // the same goes for a robot assigned to two profiles — though on prod that
  // does not happen: across Dillon's 122 and Blue Springs' 371 assigned robots,
  // every one belongs to exactly one profile.
  //
  // The teacher side is what makes the teacher breakdown honest. How a district
  // rosters decides how many teachers appear in it at all, and the ones with no
  // robot are the majority in some districts (prod SY26-27: 267 of Blue Springs'
  // 450 teacher segments, 32 of Dillon's 94). Without this they are simply rows
  // with terrible reach, and because the worklist ranks by distance from normal
  // they crowd out every teacher who has a robot and is struggling with it.
  //
  // "Working" is tense-sensitive, the same way the Overview's fleet card is: on a
  // live year it means "delivering lessons or at least connecting right now", on a
  // completed one "delivered lessons at some point during it". A dark robot is not
  // capacity, and last July's darkness is not this year's problem.
  //
  // Both maps come off ONE pass. They are the same fold over the same rows
  // differing only in which key list they credit, and two loops would drift the
  // moment the "working" test changes.
  const capacity = useMemo(() => {
    if (!robotUtil || !robotUtil.robots) return null;
    const isCurrent = schoolYear === curSY;
    const byOrgunit = new Map();
    const byTeacher = new Map();
    const credit = (map, key, working) => {
      const slot = map.get(String(key)) || { working: 0, total: 0 };
      slot.total += 1;
      if (working) slot.working += 1;
      map.set(String(key), slot);
    };
    robotUtil.robots.forEach((r) => {
      // Recency, not the last bucket: "working" for the current year means
      // working lately, and the last bucket is a sliver of a month on the 1st --
      // which read every robot in the district as parked. See fleet.recentState.
      const state = recentState(r);
      if (state === "pre") return; // not deployed in this window
      const working = isCurrent ? state !== "dark" : r.lessons_total > 0;
      (r.orgunits || []).forEach((ou) => credit(byOrgunit, ou.id, working));
      (r.teachers || []).forEach((up) => credit(byTeacher, up, working));
    });
    return { byOrgunit, byTeacher };
  }, [robotUtil, schoolYear, curSY]);

  // Quadrant model, then the status cascade on top of it. `status` is the raw
  // quadrant class, which the scatter and the Overview spark plot directly;
  // `kind` is the cascade's label, which the worklist pill reads.
  //
  // One robot lookup for every row on the Adoption worklist, at any depth.
  //
  // Keyed on the segment id's own prefix rather than on the dimension, because a
  // drill-down mixes them: school -> grade -> teacher walks orgunit, grade and
  // teacher ids in three levels of one table, and grade -> school -> teacher walks
  // grade, orgunit, teacher. One function keyed on what the row IS needs no
  // knowledge of which breakdown it was reached through.
  //
  // The three return values are distinct on purpose:
  //   {working, total}  this row owns robots, and here they are
  //   {working:0,total:0}  this row COULD own robots and owns none -- the finding
  //   null              robots are not a property of this kind of row at all
  // Collapsing the middle case into null would hide exactly what this exists to
  // surface. `teacher:unassigned` is null rather than zero: "no teacher on record"
  // is a rostering gap, and reporting it as a teacher with no robot would invent a
  // person to blame.
  const robotsFor = useMemo(() => {
    if (!capacity) return undefined;
    const maps = { orgunit: capacity.byOrgunit, teacher: capacity.byTeacher };
    const NONE = { working: 0, total: 0 };
    return (row) => {
      const [kind, key] = String(row.id).split(":");
      const map = maps[kind];
      if (!map || !key || key === "unassigned") return null;
      return map.get(key) || NONE;
    };
  }, [capacity]);

  // The status cascade reads the SAME lookup the worklist's Robots column does.
  // It used to build its own, gated on `dimension === "school"`; two functions
  // answering "how many robots does this row have" is one edit away from a table
  // that prints a number the pill beside it disagrees with. `robotsFor` already
  // returns null for the rows robots aren't a property of, so the dimension gate
  // is redundant as well as duplicated — a grade or engagement row still never
  // shows "Under-resourced".
  const quad = useMemo(
    () =>
      withKinds(buildQuad(schools), {
        isCurrent: schoolYear === curSY,
        capacityFor: robotsFor,
      }),
    [schools, robotsFor, schoolYear, curSY]
  );

  // ---- shell + routes ------------------------------------------------------
  const controls = (
    <>
      {isSuperadmin && (
        <OrgScopeSelect
          api={api}
          value={selectedOrg}
          onChange={handleSelectOrg}
        />
      )}
      <YearSelect
        year={schoolYear}
        yearOptions={yearOptions}
        onYearChange={handleSetSchoolYear}
      />
      {/* Exports the scope the two controls above resolve, over the same window
          every panel below is drawn from -- so the file and the screen cannot
          disagree. Rendered even while the spine is loading: the export runs its
          own server-side query and depends on nothing this container fetched. */}
      {/* One row per learner over the resolved scope. Not offered for all
          organizations: that file is every learner in the customer base, which
          is a data extract rather than the district report this button means,
          and the export endpoint takes no all-orgs scope for the same reason. */}
      {!allOrgs && (
        <ExportButton
          api={api}
          scope="organization"
          id={org && org.id}
          start={windowSpec.start}
          end={windowSpec.end}
          standardset={windowSpec.ssParam}
        />
      )}
    </>
  );

  // A render function rather than a node: the pages place these differently —
  // the Adoption bar wants them inline beside its lens tabs — and the choice
  // belongs to the page. Learning has no use for them since its two remaining
  // lenses scope themselves (a school picker, a grade cohort) rather than by
  // breakdown dimension.
  const segmentControls = (opts = {}) => (
    <SegmentControls
      dimension={dimension}
      setDimension={handleSetDimension}
      orgunits={orgunits}
      selectedSchool={selectedSchool}
      setSelectedSchool={handleSelectSchool}
      inline={opts.inline}
      available={dimensionsAvailable}
      unavailableReason={`Not available for ${syLabel(
        schoolYear
      )} — the enrollment ledger doesn't reach back far enough to say who taught each class.`}
    />
  );

  let body;
  // Standalone paths that predate the Adoption merge, redirected onto the lens
  // each maps to. Links to them are out in the wild, and a dashboard whose design
  // premise is "every view is a shareable deep link" cannot afford to break the
  // links it told people to share.
  //
  // Checked BEFORE the loading gate, not as routes inside it. A redirect depends on
  // nothing this page fetches; behind the barrier, a stale link would sit on a
  // spinner for the length of a full district load (~30s on Dillon, ~90s on Blue
  // Springs) before finally moving. `location.search` rides along so the org and
  // school year survive the hop.
  const legacyLens = LEGACY_LENS[location.pathname.replace(/\/+$/, "")
    .slice(match.url.replace(/\/+$/, "").length)];
  if (legacyLens)
    body = (
      <Redirect
        to={{
          pathname: `${match.url}/adoption`,
          search: withParam(location.search, "lens", legacyLens),
        }}
      />
    );
  else if (awaitingOrg)
    body = (
      <div style={{ color: "#52514e" }}>
        Select an organization above to view its dashboard.
      </div>
    );
  else if (loading)
    body = (
      <div>
        <FA color="gray" icon="spinner" spin /> Loading district data…
      </div>
    );
  else if (error)
    body = (
      <div style={{ color: STATUS.critical.color }}>
        Failed to load stats: {error}
      </div>
    );
  else if (coverage === "none")
    // Reachable by URL only — the picker disables these years. Render the reason
    // rather than panels built on a roster that has nothing to do with the
    // window. Checked after `error` so a failed load still reports itself.
    body = <div style={{ color: "#52514e" }}>{NO_HISTORY_MESSAGE}</div>;
  else
    body = (
      <Switch>
        <Route
          exact
          path={match.path}
          render={() => (
            <OverviewPage
              api={api}
              org={org}
              base={match.url}
              search={location.search}
              comp={comp}
              compLoading={compLoading}
              orgSeries={orgSeries}
              orgRecent={orgRecent}
              orgRoster={orgRoster}
              robotUtil={robotUtil}
              quad={quad}
              year={schoolYear}
              isCurrent={schoolYear === curSY}
              interval={windowSpec.interval}
              utilInterval={windowSpec.utilInterval}
              start={windowSpec.start}
              end={windowSpec.end}
              standardset={windowSpec.ssParam}
              noun={noun}
            />
          )}
        />
        {/* All-organizations serves the Overview only -- Adoption's worklist is a
            school/teacher comparison and Learning's cohorts are read against one
            district's standardset, and neither has a cross-district reading yet.
            Ordered AFTER the exact Overview route and BEFORE the three concern
            routes, which is what makes it a catch-all for exactly those: a
            Switch renders its first match, so the Overview still wins its own
            path and everything else lands back on it. The nav is hidden for the
            same reason -- see DashShell's `nav` -- so this catches deep links
            and the back button, not clicks. */}
        {allOrgs && (
          <Redirect to={{ pathname: match.url, search: location.search }} />
        )}
        <Route
          path={`${match.path}/adoption`}
          render={() => (
            <AdoptionPage
              quad={quad}
              interval={windowSpec.interval}
              noun={noun}
              segmentControls={segmentControls}
              api={api}
              org={org}
              windowSpec={windowSpec}
              dimension={dimension}
              comp={comp}
              compLoading={compLoading}
              compFailed={compFailed}
              orgunits={orgunits}
              robotsFor={robotsFor}
              syncParams={syncParams}
            />
          )}
        />
        <Route
          path={`${match.path}/learning`}
          render={() => (
            <LearningPage
              syncParams={syncParams}
              api={api}
              org={org}
              windowSpec={windowSpec}
              orgunits={orgunits}
              canScopeSchool={canScopeSchool}
              canPickStandardSet={isSuperadmin}
            />
          )}
        />
        <Route
          path={`${match.path}/fleet`}
          render={() => <FleetPage robotUtil={robotUtil} />}
        />
      </Switch>
    );

  return (
    <DashShell
      base={match.url}
      title="District insights"
      subtitle={
        allOrgs
          ? `All organizations · ${syLabel(schoolYear)} school year`
          : org && org.name
          ? `${org.name} · ${syLabel(schoolYear)} school year`
          : `${syLabel(schoolYear)} school year`
      }
      controls={controls}
      nav={allOrgs ? ALL_ORGS_NAV : undefined}
      note={[
        ...(allOrgs
          ? [
              {
                label: "All organizations",
                text:
                  "Every district on ABii, rolled up. Mastery is scored " +
                  "against the national standard set, so districts that have " +
                  "chosen a state set are re-scored here and their totals " +
                  "will not match their own dashboards. Pick a district for " +
                  "the Adoption, Learning and Fleet pages and the export.",
              },
            ]
          : []),
        ...(coverage === "partial" ? [{ text: RECONSTRUCTED_NOTE }] : []),
      ]}
    >
      {body}
    </DashShell>
  );
};

export default withAPI(DistrictDashboard);
